// @flow
import { useCombobox, type UseComboboxProps } from 'downshift';
import _ from 'underscore';
import type { Item, ItemFetcher } from './item';
import createAsyncFilter from './utils/create_async_filter';
import useAsyncDropdownFilter from './use_async_dropdown_filter';

export type UseSingleDropdownProps = UseComboboxProps<Item> & {
  items: Array<Item> | ItemFetcher,
};

const stateReducer = (state, actionAndChanges) => {
  const { changes, type } = actionAndChanges;
  switch (type) {
    /* Clear the input when an option is selected or the user cancels selecting an option */
    case useCombobox.stateChangeTypes.InputKeyDownEnter:
    case useCombobox.stateChangeTypes.ItemClick:
    case useCombobox.stateChangeTypes.InputKeyDownEscape:
    case useCombobox.stateChangeTypes.InputBlur:
      return {
        ...changes,
        inputValue: '',
      };
  }
  return changes;
};

const useSingleDropdown = (options: UseSingleDropdownProps) => {
  const itemsFetcher = _.isFunction(options.items)
    ? options.items
    : createAsyncFilter(options.items);

  const [items, loadingStatus, onSearchTermChange] =
    useAsyncDropdownFilter(itemsFetcher);

  const itemToString = (item) => item?.label || '';

  const defaultOptions = {
    itemToString,
    onInputValueChange: (e) => {
      onSearchTermChange(e.inputValue);
    },
    stateReducer: stateReducer,
    defaultHighlightedIndex: 0,
  };
  const overrides = { items };

  const comboboxOptions = Object.assign({}, defaultOptions, options, overrides);

  return {
    ...useCombobox(comboboxOptions),
    items,
    loadingStatus,
    loadInitialOptions: () => {
      onSearchTermChange('');
    },
  };
};

export default useSingleDropdown;
